import { ReadingUnitType } from '../reading-utils/enums';
import { FroalaEditorAPI } from 'src/app/services/instance/interfaces';
import { AsgardDocHtml } from './asgard-doc-html';
import { HtmlTextIterator } from '../reading-utils/html-text-iterator';
import { IMarkerContent, ILanguage } from '../interfaces';
import { EventEmitter } from '@angular/core';

export class AsgardDocFroala extends AsgardDocHtml {
  public contentWasSet: EventEmitter<boolean> = new EventEmitter();
  private froalaEditor: FroalaEditorAPI | null = null;

  constructor(docId: string) {
    super('froala-editor', true, false, docId);
  }

  get isDocumentReady(): boolean { return this.froalaEditor !== null; }

  set isEditable(value: boolean) {
    if (this.editorElement) {
      this.editorElement.setAttribute('contenteditable', value.toString());
    }

    if (this.froalaEditor && this.froalaEditor.toolbar) {
      value ? this.froalaEditor.toolbar.enable() : this.froalaEditor.toolbar.disable();
    }
  }
  get isEditable(): boolean {
    if (this.editorElement) {
      return this.editorElement.getAttribute('contenteditable') === 'true';
    }

    return false;
  }

  get editorWrapper(): HTMLElement | null {
    const editorQuery = `div.froala-editor[editorid="${this.documentId}"] > div.fr-wrapper`;
    return (window.document.querySelector(editorQuery) as HTMLIFrameElement);
  }

  get editorFrame(): HTMLIFrameElement | null {
    const editorQuery = `div.froala-editor[editorid="${this.documentId}"] > div.fr-wrapper > iframe`;
    return (window.document.querySelector(editorQuery) as HTMLIFrameElement);
  }

  get scrollContainer(): HTMLElement | null {
    const editorQuery = `${this.editorQueryStr} iframe`;
    return (window.document.querySelector(editorQuery) as HTMLIFrameElement).parentElement;
  }

  protected get textIterator(): HtmlTextIterator {
    if (this.textIteratorBuffer === null) {
      this.textIteratorBuffer = new HtmlTextIterator(this.editorSelection);
    }

    return this.textIteratorBuffer;
  }

  protected execHtmlDocumentCommand(cmd: string, arg: string | undefined = undefined) {
    const prvState = this.isEditable;
    this.isEditable = true;

    if (this.editorWin) {
      this.editorWin.document.execCommand(cmd, false, arg);
    }

    this.isEditable = prvState;
  }

  setDocumentOptins(lngMpr: (...args) => ILanguage, editor: FroalaEditorAPI) {
    super.setDocumentOptins(lngMpr);
    this.froalaEditor = editor;
    this.froalaEditor.edit.disableDesign();
  }

  setHtmlContent(html) {
    if (this.froalaEditor && this.froalaEditor.html) {
      this.froalaEditor.html.set(html);
    }
    this.contentWasSet.emit(true);
  }

  getHtmlContent(): string | null {
    if (this.editorWin) {
      return this.editorWin.document.getElementsByClassName('fr-view')[0]?.innerHTML || null;
    }

    return null;
  }

  print(): void {
    if (this.froalaEditor) {
      this.froalaEditor.print.run();
    }
  }

  addEditorEventListener(event: string, callback: (...args) => void) {
    if (this.froalaEditor && this.froalaEditor.events) {
      this.froalaEditor.events.on(event, callback);
    }
  }

  moveCursorToCurrentItemStart(itemType: ReadingUnitType) {
    if (this.hasReadingItem && this.readingItem) {
      const word = this.readingItem[0];

      if (word) {
        const dstRange = this.textIterator.getRangeForUnit(word.itemIdx, itemType);

        if (dstRange) {
          dstRange.setStart(dstRange.startContainer, dstRange.startOffset);
          dstRange.setEnd(dstRange.startContainer, dstRange.startOffset);

          if (this.editorWin) {
            this.editorWin.focus();
          }

          if (this.textIterator && this.textIterator.actvSlctn) {
            this.textIterator.actvSlctn.removeAllRanges();
            this.textIterator.actvSlctn.addRange(dstRange);
          }
        }
      }
    }
  }

  insertMarkedText(markerContent: IMarkerContent, withColors = false, withFormating = true) {
    if (markerContent.text) {
      const paragraphs = markerContent.text.split('\n');
      const styleAttr = withColors ? `style="background-color: ${markerContent.color}` : '';

      let htmlParagraphs = '';

      if (!withFormating) {
        htmlParagraphs = `<p><span ${styleAttr}">${markerContent.text}</span></p>\n`;
      }
      else {
        paragraphs.forEach(paragraphText =>
          htmlParagraphs += `<p><span ${styleAttr}">${paragraphText}</span></p>\n`
        );
      }
      let current = this.froalaEditor && this.froalaEditor.html ? this.froalaEditor.html.get(true) : null;
      if (!current) {
        current = '';
      }
      current += htmlParagraphs;

      if (this.froalaEditor && this.froalaEditor.html) {
        this.froalaEditor.html.set(current);
      }
    }
  }

  async applyDocumentChanges(docHash: string, innerHtml: string): Promise<void> {
    this.docHash = docHash;
    if (innerHtml) {
      this.setHtmlContent(innerHtml);
    }
  }

  appendText(text: string) {
    if (this.froalaEditor && this.froalaEditor.placeholder) {
      this.froalaEditor.placeholder.hide();
    }
    super.appendText(text);
  }

  public triggerArrowUpDown(ev: KeyboardEvent) {
    const elem = this.scrollContainer;
    if (elem) {
      super.triggerArrowUpDown(ev, elem);
    }
  }
}
